import { HandlerType } from "./types.js";
import { createCodec } from "../../../../dsp/codecs/index.js";
import { isq } from "../../../../isq/index.js";
export class ScalarHandler {
    _id;
    probe;
    static get = (definition) => {
        return new ScalarHandler(definition.id, definition.probes[0]);
    };
    codec;
    isqUnit;
    constructor(_id, probe) {
        this._id = _id;
        this.probe = probe;
        this.codec = createCodec(probe.codec);
        this.isqUnit = isq.getUnit(probe.unit);
    }
    id() {
        return this._id;
    }
    probes(_) {
        return [Object.assign({ isqUnit: this.isqUnit }, this.probe)];
    }
    codecs(_) {
        return [this.codec];
    }
    encodeSize() {
        return 1;
    }
    decode(encodedValue, index = 0) {
        return [this.codec.decode(encodedValue, index)];
    }
    decodeProbe(probeIndex, encodedValue, index = 0) {
        if (probeIndex === 0) {
            return this.codec.decode(encodedValue, index);
        }
        throw new Error(`Probe '${probeIndex}' is unknown to ${HandlerType.SCALAR} handler '${this.id}'. Unable to return value.`);
    }
    decodeToMap(encodedValue, index = 0) {
        return {
            [this.probe.id]: this.codec.decode(encodedValue, index),
        };
    }
}
export class AngleHandler {
    _id;
    probe;
    static get = (definition) => {
        return new AngleHandler(definition.id, definition.probes[0]);
    };
    codec;
    isqUnit;
    constructor(_id, probe) {
        this._id = _id;
        this.probe = probe;
        this.codec = createCodec(probe.codec);
        this.isqUnit = isq.getUnit(probe.unit);
    }
    id() {
        return this._id;
    }
    probes(_) {
        return [Object.assign({ isqUnit: this.isqUnit }, this.probe)];
    }
    codecs(_) {
        return [this.codec];
    }
    encodeSize() {
        return 2;
    }
    decode(encodedValue, index = 0) {
        return [this.codec.decode(encodedValue, index)];
    }
    decodeProbe(probeIndex, encodedValue, index = 0) {
        if (probeIndex === 0) {
            return this.codec.decode(encodedValue, index);
        }
        throw new Error(`Probe '${probeIndex}' is unknown to ${HandlerType.ANGLE} handler '${this.id}'. Unable to return value.`);
    }
    decodeToMap(encodedValue, index = 0) {
        return {
            [this.probe.id]: this.codec.decode(encodedValue, index),
        };
    }
}
export class VectorHandler {
    _id;
    lengthProbe;
    angleProbe;
    static get = (definition) => {
        //this order comes from legacy implementation
        return new VectorHandler(definition.id, definition.probes[0], definition.probes[1]);
    };
    lengthCodec;
    angleCodec;
    lengthIsqUnit;
    angleIsqUnit;
    constructor(_id, lengthProbe, angleProbe) {
        this._id = _id;
        this.lengthProbe = lengthProbe;
        this.angleProbe = angleProbe;
        this.lengthCodec = createCodec(lengthProbe.codec);
        this.angleCodec = createCodec(angleProbe.codec);
        this.lengthIsqUnit = isq.getUnit(lengthProbe.unit);
        this.angleIsqUnit = isq.getUnit(angleProbe.unit);
    }
    id() {
        return this._id;
    }
    probes(encodingOrder = false) {
        return encodingOrder
            ? [
                Object.assign({ isqUnit: this.angleIsqUnit }, this.angleProbe),
                Object.assign({ isqUnit: this.lengthIsqUnit }, this.lengthProbe),
            ]
            : [
                Object.assign({ isqUnit: this.lengthIsqUnit }, this.lengthProbe),
                Object.assign({ isqUnit: this.angleIsqUnit }, this.angleProbe),
            ];
    }
    codecs(encodingOrder = false) {
        return encodingOrder
            ? [this.angleCodec, this.lengthCodec]
            : [this.lengthCodec, this.angleCodec];
    }
    encodeSize() {
        return 3;
    }
    decode(encodedValue, index = 0) {
        const angle = this.angleCodec.decode(encodedValue, index);
        const length = this.lengthCodec.decode(encodedValue, index + 2);
        //this order comes from legacy implementation
        return [length, angle];
    }
    decodeProbe(probeIndex, encodedValue, index = 0) {
        switch (probeIndex) {
            case 0: //length
                return this.lengthCodec.decode(encodedValue, index + 2);
            case 1: //angle
                return this.angleCodec.decode(encodedValue, index);
        }
        throw new Error(`Probe '${probeIndex}' is unknown to ${HandlerType.VECTOR} handler '${this.id}'. Unable to return value.`);
    }
    decodeToMap(encodedValue, index = 0) {
        return {
            [this.lengthProbe.id]: this.lengthCodec.decode(encodedValue, index + 2),
            [this.angleProbe.id]: this.angleCodec.decode(encodedValue, index),
        };
    }
}
export class LevelHandler {
    _id;
    probe;
    static get = (definition) => {
        return new LevelHandler(definition.id, definition.probes[0]);
    };
    codec;
    isqUnit;
    constructor(_id, probe) {
        this._id = _id;
        this.probe = probe;
        this.codec = createCodec(probe.codec);
        this.isqUnit = isq.getUnit(probe.unit);
    }
    id() {
        return this._id;
    }
    probes(_) {
        return [Object.assign({ isqUnit: this.isqUnit }, this.probe)];
    }
    codecs(_) {
        return [this.codec];
    }
    encodeSize() {
        return 1;
    }
    decode(encodedValue, index = 0) {
        return [this.codec.decode(encodedValue, index)];
    }
    decodeProbe(probeIndex, encodedValue, index = 0) {
        if (probeIndex === 0) {
            return this.codec.decode(encodedValue, index);
        }
        throw new Error(`Probe '${probeIndex}' is unknown to ${HandlerType.LEVEL} handler '${this.id}'. Unable to return value.`);
    }
    decodeToMap(encodedValue, index = 0) {
        return {
            [this.probe.id]: this.codec.decode(encodedValue, index),
        };
    }
}
export const createHandler = (definition) => {
    switch (definition.type) {
        case HandlerType.SCALAR:
            return ScalarHandler.get(definition);
        case HandlerType.ANGLE:
            return AngleHandler.get(definition);
        case HandlerType.VECTOR:
            return VectorHandler.get(definition);
        case HandlerType.LEVEL:
            return LevelHandler.get(definition);
    }
};
